import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CaseStudyModal = ({ caseStudy, isOpen, onClose }) => {
  if (!isOpen || !caseStudy) return null;

  const getIndustryIcon = (industry) => {
    const iconMap = {
      'Healthcare': 'Heart',
      'Financial Services': 'Building2',
      'Manufacturing': 'Factory',
      'Retail': 'ShoppingCart',
      'Technology': 'Cpu',
      'Education': 'GraduationCap'
    };
    return iconMap?.[industry] || 'Building';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-executive-lg rounded-xl">
          {/* Header */}
          <div className="relative">
            <div className="h-64 overflow-hidden">
              <Image
                src={caseStudy?.image}
                alt={`${caseStudy?.company} case study`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white"
              iconName="X"
            />

            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center space-x-3 mb-4">
                <Icon 
                  name={getIndustryIcon(caseStudy?.industry)} 
                  size={20} 
                  className="text-white" 
                />
                <span className="text-white font-medium">{caseStudy?.industry}</span>
                <span className="text-white/80">•</span>
                <span className="text-white/80">{caseStudy?.companySize}</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {caseStudy?.company}
              </h1>
              <p className="text-white/90 text-lg">
                {caseStudy?.subtitle}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 p-6 bg-gray-50 rounded-xl">
              {caseStudy?.keyMetrics?.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-brand-primary mb-2">
                    {metric?.value}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {metric?.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Challenge, Solution, Results */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
                  <Icon name="AlertTriangle" size={24} className="mr-3 text-orange-500" />
                  Challenge
                </h2>
                <p className="text-text-secondary leading-relaxed">
                  {caseStudy?.detailedChallenge}
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
                  <Icon name="Lightbulb" size={24} className="mr-3 text-blue-500" />
                  Solution
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  {caseStudy?.detailedSolution}
                </p>
                
                {/* Implementation Steps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {caseStudy?.implementationSteps?.map((step, index) => (
                    <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-text-primary mb-1">{step?.title}</h4>
                        <p className="text-sm text-text-secondary">{step?.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
                  <Icon name="TrendingUp" size={24} className="mr-3 text-green-500" />
                  Results
                </h2>
                <p className="text-text-secondary leading-relaxed mb-6">
                  {caseStudy?.detailedResults}
                </p>

                {/* Testimonial */}
                <div className="bg-brand-primary/5 border-l-4 border-brand-primary p-6 rounded-r-lg">
                  <blockquote className="text-lg text-text-primary italic mb-4">
                    "{caseStudy?.testimonial?.quote}"
                  </blockquote>
                  <div className="flex items-center space-x-4">
                    <Image
                      src={caseStudy?.testimonial?.avatar}
                      alt={caseStudy?.testimonial?.author}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-text-primary">
                        {caseStudy?.testimonial?.author}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {caseStudy?.testimonial?.role}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-text-primary mb-3">Technologies & Approaches</h3>
              <div className="flex flex-wrap gap-2">
                {caseStudy?.tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="default"
                className="flex-1 bg-brand-primary hover:bg-brand-primary/90"
                iconName="Download"
                iconPosition="left"
              >
                Download Executive Summary
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                iconName="Share"
                iconPosition="left"
              >
                Share Case Study
              </Button>
              <Button
                variant="outline"
                iconName="MessageCircle"
                iconPosition="left"
              >
                Discuss Similar Project
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;